import { ReactElement } from "react";
import { User } from "../../types/user.type";
import { STRIPE_BACKGROUND } from "../Product/shared/constants";
import { ROLE_COLOR_MAP } from "./data";
import Chip, { ChipProps } from "@mui/material/Chip";
import IconButton, { IconButtonProps } from "@mui/material/IconButton";
import Stack from "@mui/material/Stack";
import TableCell from "@mui/material/TableCell";
import TableRow from "@mui/material/TableRow";
import Typography from "@mui/material/Typography";
import EditIcon from "@mui/icons-material/Edit";
import BlockIcon from "@mui/icons-material/Block";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import LockResetIcon from "@mui/icons-material/LockReset";
import DeleteIcon from "@mui/icons-material/Delete";

const EMPTY_PLACEHOLDER = "-";

interface StatusDisplay {
  label: string;
  color: ChipProps["color"];
}

interface ToggleAction {
  label: string;
  color: IconButtonProps["color"];
  icon: ReactElement;
}

const ENABLED_STATUS: StatusDisplay = { label: "ใช้งาน", color: "success" };
const DISABLED_STATUS: StatusDisplay = { label: "ปิดใช้งาน", color: "default" };

const DISABLE_ACTION: ToggleAction = {
  label: "ปิดใช้งาน",
  color: "error",
  icon: <BlockIcon fontSize="small" />,
};
const ENABLE_ACTION: ToggleAction = {
  label: "เปิดใช้งาน",
  color: "success",
  icon: <CheckCircleOutlineIcon fontSize="small" />,
};

interface UserRowProps {
  user: User;
  striped: boolean;
  onEdit: (userId: string) => void;
  onToggleEnabled: (user: User) => void;
  onResetPassword: (user: User) => void;
  /** Absent unless the viewer is MASTER — only MASTER may delete an account. */
  onDelete?: (user: User) => void;
}

export default function UserRow({
  user,
  striped,
  onEdit,
  onToggleEnabled,
  onResetPassword,
  onDelete,
}: UserRowProps) {
  const handleEdit = () => onEdit(user.id);
  const handleToggleEnabled = () => onToggleEnabled(user);
  const handleResetPassword = () => onResetPassword(user);
  const handleDelete = () => onDelete?.(user);
  const status = user.enabled ? ENABLED_STATUS : DISABLED_STATUS;
  const toggleAction = user.enabled ? DISABLE_ACTION : ENABLE_ACTION;

  return (
    <TableRow
      sx={{
        bgcolor: striped ? STRIPE_BACKGROUND : "#fff",
        "& td, & th": { padding: "8px" },
      }}
    >
      <TableCell>{user.username}</TableCell>
      <TableCell>{user.name}</TableCell>
      <TableCell>{user.email ?? EMPTY_PLACEHOLDER}</TableCell>
      <TableCell>{user.branch}</TableCell>
      <TableCell>{user.routeCode || EMPTY_PLACEHOLDER}</TableCell>
      <TableCell sx={{ width: "10%" }}>
        <Stack direction="row" flexWrap="wrap" gap={1}>
          {user.roles.map((role) => (
            <Chip
              key={role}
              label={role}
              variant="outlined"
              color="primary"
              sx={{
                width: "100%",
                textTransform: "capitalize",
                borderColor: ROLE_COLOR_MAP[role],
                color: ROLE_COLOR_MAP[role],
              }}
            />
          ))}
        </Stack>
      </TableCell>
      <TableCell>
        <Chip
          size="small"
          variant="outlined"
          color={status.color}
          label={status.label}
        />
      </TableCell>
      <TableCell>
        <Stack direction="row" gap={1}>
          <IconButton
            size="small"
            color="primary"
            sx={{ p: 1.5 }}
            onClick={handleEdit}
          >
            <EditIcon fontSize="small" />
            <Typography variant="subtitle2">แก้ไข</Typography>
          </IconButton>
          <IconButton
            size="small"
            color={toggleAction.color}
            sx={{ p: 1.5 }}
            onClick={handleToggleEnabled}
          >
            {toggleAction.icon}
            <Typography variant="subtitle2">{toggleAction.label}</Typography>
          </IconButton>
          <IconButton
            size="small"
            color="warning"
            sx={{ p: 1.5 }}
            onClick={handleResetPassword}
          >
            <LockResetIcon fontSize="small" />
            <Typography variant="subtitle2">รีเซ็ตรหัสผ่าน</Typography>
          </IconButton>
          {onDelete && (
            <IconButton
              size="small"
              color="error"
              sx={{ p: 1.5 }}
              onClick={handleDelete}
            >
              <DeleteIcon fontSize="small" />
              <Typography variant="subtitle2">ลบ</Typography>
            </IconButton>
          )}
        </Stack>
      </TableCell>
    </TableRow>
  );
}
